import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import KPICards from '../../components/dashboard/KPICards';
import PlanMixChart from '../../components/dashboard/PlanMixChart';
import EvolutionChart from '../../components/dashboard/EvolutionChart';
import AltasBajasChart from '../../components/dashboard/AltasBajasChart';
import TotalSuscriptoresTable from '../../components/dashboard/TotalSuscriptoresTable';
import EditRevenueModal from '../../components/dashboard/EditRevenueModal';
import Exclusive8080Breakdown from '../../components/dashboard/Exclusive8080Breakdown';
import DashboardFilters from '../../components/dashboard/DashboardFilters';
import VentasEjecutivas from '../../components/dashboard/VentasEjecutivas';
import DashboardErrorBoundary from '../../components/dashboard/DashboardErrorBoundary';
import dashboardService from '../../services/dashboardService';
import { DashboardDataRaw, DateRange, Granularity } from '../../types/Dashboard';
import {
  today,
  addMonths,
  exclusive8080AutoRevenue,
  lastDayOfMonth,
  manualOverrideForMonth,
} from '../../components/dashboard/dashboardHelpers';
import { formatNumber } from '../../components/dashboard/dashboardUi';
import { useAuth } from '../../contexts/AuthContext';
import { Alert, Badge, Button, Card, IconSwap, staggerParent, enterChild } from '../../components/ui';
import { PencilIcon, RefreshIcon, SpinnerIcon } from '../../components/icons';

function formatFetchedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const EXCLUSIVE_PLAN = 'Exclusive 8080';

/** Esqueleto con la misma silueta que el contenido real, para que no salte al cargar. */
const DashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    <Card className="animate-pulse p-4">
      <div className="h-9 w-full rounded-lg bg-slate-100" />
    </Card>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse p-5">
          <div className="mb-3.5 h-2.5 w-2/3 rounded bg-slate-100" />
          <div className="h-7 w-1/2 rounded bg-slate-100" />
        </Card>
      ))}
    </div>
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="animate-pulse p-6 lg:col-span-2">
        <div className="h-52 rounded-xl bg-slate-100" />
      </Card>
      <Card className="animate-pulse p-6">
        <div className="h-52 rounded-xl bg-slate-100" />
      </Card>
    </div>
    <Card className="animate-pulse p-6">
      <div className="h-72 rounded-xl bg-slate-100" />
    </Card>
  </div>
);

const DashboardPage: React.FC = () => {
  const { userRole } = useAuth();
  const canEditRevenue = userRole === 'admin' || userRole === 'manager';

  const [range, setRange] = useState<DateRange>({
    from: addMonths(today(), -6),
    to: today(),
  });
  const [granularity, setGranularity] = useState<Granularity>('month');
  const [data, setData] = useState<DashboardDataRaw | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * `silent` mantiene el contenido en pantalla mientras se vuelve a pedir.
   * Un refresco o un guardado no tienen por qué devolver la página a
   * esqueletos: el layout salta y el usuario pierde dónde estaba mirando.
   */
  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await dashboardService.getDashboardData();
      if (res.success) {
        setData({
          fetchedAt: res.fetchedAt,
          stale: res.stale,
          premium: res.premium,
          freeActivos: res.freeActivos,
          precios: res.precios,
          manualRevenueHistory: res.manualRevenueHistory,
        });
      } else {
        setError(res.message ?? 'Error al cargar el dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado al cargar el dashboard');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dashboardService.refreshCache();
      await fetchData({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al refrescar el cache');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSaved = useCallback(() => {
    fetchData({ silent: true });
  }, [fetchData]);

  /** Activos del 8080 sin fecha de nacimiento: no suman al revenue auto-calculado. */
  const missingBirthdate = useMemo(() => {
    if (!data) return 0;
    const snapshotEoM = lastDayOfMonth(range.to);
    const override = manualOverrideForMonth(
      data.manualRevenueHistory[EXCLUSIVE_PLAN] ?? [],
      snapshotEoM,
    );
    // Con override manual el cálculo por edad no se usa, así que no hay nada que avisar.
    if (override) return 0;
    return exclusive8080AutoRevenue(data.premium, snapshotEoM, EXCLUSIVE_PLAN).missingBirthdate;
  }, [data, range.to]);

  const showSkeleton = loading && !data;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Header />

      <main className="container mx-auto flex-grow px-4 py-8">
        <motion.div variants={staggerParent} initial="hidden" animate="visible" className="space-y-6">
          {/* Encabezado */}
          <motion.div
            variants={enterChild}
            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="min-w-0">
              <h1 className="font-poppins text-2xl font-semibold text-gappex-secondary">
                Dashboard de planes
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Cómo viene la cartera premium: cuántos son, cuánto facturan y cómo se mueven altas y
                bajas.
              </p>
              {data && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-xs tabular-nums text-slate-400">
                    Corte de datos: {formatFetchedAt(data.fetchedAt)}
                  </span>
                  {data.stale && (
                    <Badge
                      tone="warning"
                      title="El cache venció. Los números pueden estar atrasados; refrescá para volver a pedirlos a Salesforce."
                    >
                      Cache vencido
                    </Badge>
                  )}
                  {refreshing && (
                    <Badge tone="info">
                      <SpinnerIcon className="me-1 h-3 w-3" strokeWidth={2} />
                      Actualizando
                    </Badge>
                  )}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-wrap gap-2">
              {userRole === 'admin' && (
                <Button
                  variant="secondary"
                  onClick={handleRefresh}
                  disabled={refreshing || showSkeleton}
                  title="Vuelve a pedir los datos a Salesforce y rearma el cache"
                  icon={
                    <IconSwap swapKey={refreshing ? 'busy' : 'idle'}>
                      {refreshing ? <SpinnerIcon /> : <RefreshIcon />}
                    </IconSwap>
                  }
                >
                  {refreshing ? 'Actualizando…' : 'Refrescar cache'}
                </Button>
              )}
              {canEditRevenue && (
                <Button
                  variant="primary"
                  onClick={() => setModalOpen(true)}
                  icon={<PencilIcon />}
                  title={`Cargar o corregir el revenue mensual del ${EXCLUSIVE_PLAN}`}
                >
                  Revenue {EXCLUSIVE_PLAN}
                </Button>
              )}
            </div>
          </motion.div>

          {/* Filtros */}
          {!showSkeleton && (
            <motion.div variants={enterChild}>
              <DashboardFilters
                range={range}
                granularity={granularity}
                onRangeChange={setRange}
                onGranularityChange={setGranularity}
              />
            </motion.div>
          )}

          {/* Avisos */}
          <AnimatePresence initial={false}>
            {error && (
              <Alert
                key="error"
                tone="error"
                title="No se pudo cargar el dashboard"
                action={
                  <Button variant="secondary" onClick={() => fetchData()}>
                    Reintentar
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            {data && missingBirthdate > 0 && (
              <Alert
                key="missing-birthdate"
                tone="warning"
                title={`${formatNumber(missingBirthdate)} suscriptor${
                  missingBirthdate === 1 ? '' : 'es'
                } del ${EXCLUSIVE_PLAN} sin fecha de nacimiento`}
              >
                No suman al revenue auto-calculado del mes. Cargá el <code>PersonBirthdate</code> en
                Salesforce, o definí un revenue manual para este mes.
              </Alert>
            )}
          </AnimatePresence>

          {/* Contenido */}
          {showSkeleton ? (
            <DashboardSkeleton />
          ) : (
            data && (
              <DashboardErrorBoundary>
                {/* Sin initial/animate propios: hereda el estado del contenedor de
                    la página y encadena su propio stagger sobre los hijos. */}
                <motion.div variants={staggerParent} className="space-y-6" aria-busy={refreshing}>
                  <motion.div variants={enterChild}>
                    <KPICards data={data} range={range} />
                  </motion.div>

                  <motion.div variants={enterChild} className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                      <PlanMixChart data={data} range={range} />
                    </div>
                    <TotalSuscriptoresTable data={data} range={range} />
                  </motion.div>

                  <motion.div variants={enterChild}>
                    <EvolutionChart data={data} range={range} granularity={granularity} />
                  </motion.div>

                  <motion.div variants={enterChild}>
                    <AltasBajasChart data={data} range={range} granularity={granularity} />
                  </motion.div>

                  {canEditRevenue && (
                    <motion.div variants={enterChild}>
                      <Exclusive8080Breakdown data={data} range={range} planName={EXCLUSIVE_PLAN} />
                    </motion.div>
                  )}

                  {/* Ventas del equipo comercial: datos de la app, no de Salesforce. */}
                  <motion.div variants={enterChild} className="border-t border-slate-200 pt-6">
                    <VentasEjecutivas range={range} granularity={granularity} />
                  </motion.div>
                </motion.div>
              </DashboardErrorBoundary>
            )
          )}
        </motion.div>
      </main>

      {/* Modal siempre montado al final, visible solo si admin o manager */}
      <EditRevenueModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        planName={EXCLUSIVE_PLAN}
        initialHistory={data?.manualRevenueHistory[EXCLUSIVE_PLAN] ?? []}
      />
    </div>
  );
};

export default DashboardPage;
